// @flow

import React, { useEffect, useRef } from "react";
import { localize } from "redux-i18n";
import Ajv from "ajv";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Col } from "@components";
import { SchemaForm } from "@components";
import type { EmployeeType } from "@modules/employee/types";
import { removeNullAndUndefinedProps } from "@transformers/object";
import type { PropertiesSchema } from "@types";
import { isAfter, isEqual } from "date-fns";
import config from "@config";
type Props = {
  onDismissHandler: () => void,
  onSubmit: (form: Object) => void,
  schema: ?PropertiesSchema,
  uiSchema: Object | null,
  employee: EmployeeType,
  handleChange: (form: Object) => void,
  ...PropsFromLocalize,
};

export function AddEmployeeModal(props: Props) {
  let validate;
  const formRef = useRef(null);
  useEffect(() => {
    if (props.schema && !validate) {
      validate = new Ajv().compile(props.schema);
    }
  }, [props]);

  function isValid() {
    if (!validate) {
      return false;
    }

    return validate(removeNullAndUndefinedProps(props.employee));
  }

  function submitForm() {
    formRef.current.submit();
  }

  function handleSubmit(form: Object) {
    props.onSubmit(form);
  }

  function uiSchema() {
    const { dateOfBirth, joinDate } = props.employee || {};

    return {
      id: { "ui:widget": "hidden" },
      hasHardOfHearing: {
        "ui:widget": "select",
      },
      isNonServiceUser: {
        "ui:widget": "toggle",
      },
      isActive: {
        "ui:widget": "hidden",
      },
      hasDisability: {
        "ui:widget": "select",
      },
      dateOfBirth: {
        "ui:widget": "date-picker",
        "ui:options": {
          dateFormat: config.dateFormatBE,
        },
      },
      joinDate: {
        "ui:widget": "date-picker",
        "ui:options": {
          dateFormat: config.dateFormatBE,
          minDate: dateOfBirth || null,
        },
      },
      leaveDate: {
        "ui:widget": "date-picker",
        "ui:options": {
          dateFormat: config.dateFormatBE,
          minDate: joinDate ? new Date(joinDate) : null,
        },
      },
      workEmail: {
        "ui:title": props.t("EMAIL_ADDRESS"),
      },
      homeEmail: {
        "ui:title": props.t("BACKUP_EMAIL"),
      },
      "ui:order": [
        "firstName",
        "lastName",
        "dateOfBirth",
        "gender",
        "hasDisability",
        "hasHardOfHearing",
        "employeeRefPrimary",
        "employeeRefSecondary",
        "isNonServiceUser",
        "employmentTypeId",
        "joinDate",
        "leaveDate",
        "question",
        "answer",
        "homeLandline",
        "homeMobile",
        "homeEmail",
        "notificationMobile",
        "workEmail",
        "isActive",
        "id",
      ],
    };
  }

  function validateSchema(formData: Object, errors: Object) {
    const { dateOfBirth, joinDate, leaveDate } = formData;

    if (isAfter(new Date(dateOfBirth), new Date(joinDate))) {
      errors.joinDate.addError("Date should be after Date Of Birth");
    }

    if (
      isAfter(new Date(joinDate), new Date(leaveDate)) ||
      isEqual(new Date(joinDate), new Date(leaveDate))
    ) {
      errors.leaveDate.addError("Date should be after Employment Start Date");
    }

    return errors;
  }

  return (
    <Modal className="min-modal-width">
      <ModalHeader toggle={props.onDismissHandler}>{props.t("ADD_NEW_EMPLOYEE")}</ModalHeader>
      <ModalBody>
        <SchemaForm
          ref={formRef}
          onChange={props.handleChange}
          schema={props.schema}
          onSubmit={handleSubmit}
          uiSchema={uiSchema()}
          formData={props.employee}
          externalSubmit
          validate={validateSchema}
          columns={2}
        />
      </ModalBody>
      <ModalFooter className="d-flex justify-content-end">
        <Button color="secondary" onClick={props.onDismissHandler}>
          {props.t("CANCEL")}
        </Button>
        <Button materialIcon="add_box" onClick={submitForm} disabled={!isValid}>
          {props.t("ADD")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default localize()(AddEmployeeModal);
